import { useAuth0 } from '@auth0/auth0-react';
import Authentication from 'common/components/auth/Authentication';
import ErrorPage from 'common/components/errorPages/ErrorPage';
import { Spinner } from 'common/components/loader/Spinner';
import { PROJECT_ID_PREFIX } from 'common/constants';
import { Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from 'common/store/slices/auth.slice';
import { useResize } from 'common/hooks/resize';
import { useIntercom } from 'common/hooks/intercom';
import { HelmetHeader } from 'common/components/layouts/HelmetHeader';
import { APP_ROOT } from 'routes/routes.const';
import { useFullStory } from 'common/hooks/fullStory';
import { isLocalhost } from 'common/hooks/utils';

import AuthorizedRoute, { ignoreRoutes } from '../../routes/AuthorizedRoute';
import { clearAuthToken } from 'common/hooks/auth';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { selectModules } from 'common/store/slices/page-config.slice';
import { showToast } from 'common/store/slices/notification.slice';
import { NotificationType } from 'common/models/notification.model';
import useSignOut from 'common/hooks/useSignOut';
import { PowerpointExportProgressBar } from '../../common/components/widgets/exports/PowerpointExportProgressBar';
import { PowerpointExportServiceWorkerListener } from '../../common/components/flyouts/PowerpointExport/PowerpointExportServiceWorkerListener';
import { ExcelExportProgressBar } from '../../common/components/widgets/exports/ExcelExportProgressBar';

const AppPage = () => {
  const dispatch = useDispatch();
  const [fullStoryAvailable, setFullStoryAvailable] = useState(false);
  const { pathname, search: searchParams } = useLocation();
  const { isLoading, isAuthenticated } = useAuth0();
  const { isMobile } = useResize();
  const user = useSelector(selectUser);
  const { initiateIntercom } = useIntercom();
  const { initiateFullStory } = useFullStory();
  const localhost = isLocalhost();
  const { isSigningOut } = useSignOut();
  const modules = useSelector(selectModules);
  const { activeProject, lastActiveModule, lastActiveProject } = useModuleProjectContext();

  // Recuring check if intercom is available
  useEffect(() => {
    const checkFullStory = setInterval(() => {
      if (localhost) {
        clearInterval(checkFullStory);
        return;
      }

      if (window.FS) {
        setFullStoryAvailable(true);
        clearInterval(checkFullStory);
      }
    }, 1000);

    return () => clearInterval(checkFullStory);
  }, [localhost]);

  useEffect(() => {
    // Initiate intercom if user is logged in and intercom is available
    if (
      user?.email &&
      user?.externalName &&
      user?.companyName &&
      !isMobile &&
      activeProject?.projectClientName
    ) {
      try {
        initiateIntercom();
      } catch (error) {
        console.error('Intercom error:', error);
      }
    }
  }, [user, isMobile, initiateIntercom, activeProject?.projectClientName]);

  if (
    !localhost &&
    user?.id &&
    user?.companyName &&
    activeProject?.projectClientName &&
    fullStoryAvailable
  ) {
    initiateFullStory();
  }

  if (!isLoading && !isAuthenticated && !ignoreRoutes.includes(pathname) && !isSigningOut) {
    clearAuthToken();

    if (pathname !== APP_ROOT) {
      localStorage.setItem('previousKwelloPath', pathname);
      localStorage.setItem('previousSearchValues', searchParams);
      return <Navigate to={APP_ROOT} replace={true} />;
    }

    const loginError = localStorage.getItem('loginError');
    if (loginError) {
      dispatch(
        showToast({
          id: 'login-error',
          text: loginError,
          type: NotificationType.ERROR,
        })
      );
      localStorage.removeItem('loginError');
    }

    return <Authentication />;
  }

  // If no module or project selected, find appropriate project to navigate to
  if (!isLoading && isAuthenticated && pathname === APP_ROOT) {
    const previousPath = localStorage.getItem('previousKwelloPath');
    const previousSearchValues = localStorage.getItem('previousSearchValues');

    if (previousPath) {
      // Cold Auth0 entries return to `/` after AppPage saved the original path
      // and query above. Restore them before the last-active/default project
      // branches can mount their temporary AuthorizedRoute.
      //
      // #141 made `/app/profile-capture` translation-only for warm sessions,
      // but the #149 review found that a cold return still bootstrapped at `/`
      // before restoring that legacy path. Consuming it here completes the
      // single-owner invariant documented in incident post-mortem #150: the
      // final modern Capture route alone owns token/app-config bootstrap.
      localStorage.removeItem('previousKwelloPath');
      localStorage.removeItem('previousSearchValues');

      return <Navigate to={`${previousPath}${previousSearchValues ?? ''}`} replace={true} />;
    }

    // Try to navigate to the last active project
    if (lastActiveModule && lastActiveProject) {
      return (
        <Navigate
          to={`${lastActiveModule.route}/${PROJECT_ID_PREFIX}${lastActiveProject.moduleProjectID}`}
          replace={true}
        />
      );
    }

    // If they don't have previous project, navigate to Insights if they have it
    const insightsModule = modules?.find(
      (module) => module.moduleName?.toLowerCase() === 'insights'
    );
    if (insightsModule) {
      return (
        <Navigate
          to={`/insights/${PROJECT_ID_PREFIX}${insightsModule.projects[0].moduleProjectID}`}
          replace={true}
        />
      );
    }

    const firstModule = modules?.filter((module) => !module.routeIsExternalLink)[0];
    const firstProject = firstModule?.projects[0];

    if (firstProject) {
      return (
        <Navigate
          to={`${firstModule.route}/${PROJECT_ID_PREFIX}${firstProject.moduleProjectID}`}
          replace={true}
        />
      );
    }

    return (
      <AuthorizedRoute>
        <Spinner />
      </AuthorizedRoute>
    );
  }

  return (
    <Suspense>
      <HelmetHeader />
      <ErrorBoundary fallback={<ErrorPage />}>
        <PowerpointExportServiceWorkerListener />
        <PowerpointExportProgressBar />
        <ExcelExportProgressBar />
        <Outlet />
      </ErrorBoundary>
    </Suspense>
  );
};

export default AppPage;
